<template>
    <!--h4 class="text-success text-center">Api integrated</h4-->
    <div class="container-fluid px-4">
        <div class="mb-3">
            <BreadcrumbNav />
        </div>
        <PagedActions :showSkills="false" />

        <!-- BaseTable -->
        <BaseTable :rows="skills" :columns="columns" :loading="loading" searchable sortable :selectable="false"
            :pagination="pagination" @changePage="onChangePage" @updatePageSize="onUpdatePageSize"
            @searchChanged="onSearchChanged">

            <template #col-skillSummary="{ row }">
                <button class="btn btn-sm btn-outline-success my-0" @click="openSkillSummary(row)">
                    <i class="material-icons text-sm align-middle">summarize</i> Summary
                </button>
            </template>

            <!-- Action buttons for each row -->
            <template #actions="{ row }">
                <button class="btn btn-sm my-0 py-0" @click="viewSkill(row.id)" title="View Skill">
                    <i class="material-icons text-lg text-blue" style="color: #2196F3;">visibility</i>
                </button>
                <button v-if="canModifySkill(row.id).canEdit" class="btn btn-sm my-0 py-0" @click="editSkill(row.id)" title="Edit Skill">
                    <i class="material-icons text-lg text-orange" style="color: #e3c9;">edit</i>
                </button>
                <button v-if="canDeleteSkill(row.id)" class="btn btn-sm my-0 py-0" @click="openDeleteModal(row)" title="Delete Skill">
                    <i class="material-icons text-lg text-danger" style="color: #FF0000;">delete</i>
                </button>
            </template>
        </BaseTable>

        <DeleteModal v-model:show="showDeleteModal" :itemName="selectedSkill?.skillRole" @confirm="deleteSkill" />

        <!-- Delete error alert -->
        <div v-if="deleteError" class="delete-error-overlay">
          <div class="delete-error-card">
            <div class="delete-error-header">
              <i class="material-icons">shield</i>
              <span>Deletion Safeguard</span>
              <button class="delete-error-close" @click="deleteError = null">&times;</button>
            </div>
            <div class="delete-error-body">
              <div class="delete-error-icon-wrap">
                <i class="material-icons">error_outline</i>
              </div>
              <p class="delete-error-msg">{{ deleteError }}</p>
              <div class="delete-error-help">
                <h6><i class="material-icons">lightbulb</i> What you need to do</h6>
                <!-- Case: skill assigned to users -->
                <ol v-if="deleteErrorType === 'users'">
                  <li>Click <strong>Edit</strong> on this skill to open its details.</li>
                  <li>In the skill editor, <strong>remove all persons</strong> currently assigned to the skill.</li>
                  <li>Save the skill, then return here and delete it.</li>
                </ol>
                <!-- Case: skill used in workgroups -->
                <ol v-else-if="deleteErrorType === 'workgroups'">
                  <li>Go to <strong>Manage Activities/Events</strong> and find the Activity(ies) that use this skill.</li>
                  <li>Within each Activity, locate the <strong>Workgroup(s)</strong> referencing this skill.</li>
                  <li><strong>Delete or modify</strong> those Workgroups so they no longer use this skill.</li>
                  <li>Once no Workgroups reference the skill, return here and delete it.</li>
                </ol>
                <!-- Generic fallback -->
                <ol v-else>
                  <li>Review the error message above to understand what is using this skill.</li>
                  <li>Remove all references to this skill, then return here and delete it.</li>
                </ol>
                <p class="delete-error-note">
                  <i class="material-icons" style="font-size:14px;vertical-align:middle;">info</i>
                  This safeguard is in place to prevent accidental deletions that could affect user assignments, workgroups, and scheduling.
                </p>
              </div>
            </div>
            <div class="delete-error-footer">
              <button class="btn btn-sm btn-primary" @click="deleteError = null">Got it</button>
            </div>
          </div>
        </div>

        <SummaryModal v-model="showSkillSummaryModal" :title="skillSummaryTitle" :summaryList="skillSummaryData" />

        <!-- Edit Skill Modal -->
        <ContentModal v-model="showEditModal" :title="viewOnly ? 'View Skill' : 'Edit Skill'" size="xl" :showClose="true">
            <EditSkill v-if="selectedSkillId" :key="selectedSkillId" :skillId="selectedSkillId" :locationId="locationId" :viewOnly="viewOnly" />
        </ContentModal>

        

    </div>
</template>

<script setup>
import BaseTable from "@/components/Table/Table.vue";
import DeleteModal from "@/components/Modal/ConfirmationModal.vue";
import ContentModal from "@/components/Modal/ContentModal.vue";
import PagedActions from "./PagedActions.vue";
import EditSkill from "./EditSkill.vue";
import { ref, onMounted, computed, watch } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import { useRoute } from "vue-router";
import { usePermissions } from "@/composables/usePermissions";
import { useBreadcrumb } from "@/composables/useBreadcrumb";
import BreadcrumbNav from "@/components/common/BreadcrumbNav.vue";
import SummaryModal from "@/components/Modal/SummaryModal.vue";

const toast = useToast();
const store = useStore();
const route = useRoute();
const { canModifySkill, canDeleteSkill } = usePermissions();
const { setTrail, clearTrail } = useBreadcrumb();

// Set top-level breadcrumb for this list page
clearTrail();
setTrail([
  { label: 'Manage Skills/Roles', icon: 'workspace_premium' },
]);
const searchTerm = ref("");
const showDeleteModal = ref(false);
const showEditModal = ref(false);
const viewOnly = ref(false);
const selectedSkill = ref(null);
const selectedSkillId = ref(null);
const loading = ref(true);
const deleteError = ref(null);
const deleteErrorType = ref(null);
const locationId = route.params.locationid;
const skills = computed(() => store.getters.allSkills);

const columns = [
    { key: "id", label: "ID" },
    { key: "skillRole", label: "Skill/Role" },
    { key: "skillSummary", label: "Summary" },
    { key: "default", label: "Default" },
    { key: "volunteerCount", label: "Volunteer Count" },
    { key: "autoAdd", label: "Auto Add" },
    { key: "publicView", label: "Public View" },
    { key: "preSelect", label: "Pre-Select" },
    { key: "description", label: "Description" },
];

const pagination = computed(() => store.getters.pagination);

const showSkillSummaryModal = ref(false);
const skillSummaryData = ref([]);
const skillSummaryTitle = ref('');

const openSkillSummary = async (row) => {
    skillSummaryTitle.value = `Summary: ${row.skillRole}`;
    try {
        skillSummaryData.value = await store.dispatch('fetchSkillSummary', row.id);
        showSkillSummaryModal.value = true;
    } catch (e) {
        console.error('Error loading skill summary:', e);
    }
};

// ✅ Added: Unified fetch method to handle search and pagination
const fetchSkills = async (page = 1, pageSize = pagination.value.pageSize || 10) => {
    loading.value = true;
    try {
        await store.dispatch("getSkillsByLocation", {
            locationId,
            search: searchTerm.value || "",
            page,
            pageSize,
        });
    } catch (error) {
        console.error("Error fetching skills:", error);
        toast.error("Failed to load skills", { position: "top-right" });
    } finally {
        loading.value = false;
    }
};

const onChangePage = async (page) => {
    if (!page) return;
    await fetchSkills(page);
};

const onUpdatePageSize = async (size) => {
    await fetchSkills(1, size);
};

// ✅ Added: Handle table search input
const onSearchChanged = async (term) => {
    searchTerm.value = term;
    await fetchSkills(1);
};

const openDeleteModal = (skill) => {
    selectedSkill.value = skill;
    showDeleteModal.value = true;
};

const editSkill = (id) => {
    selectedSkillId.value = id;
    viewOnly.value = false;
    showEditModal.value = true;
};

const viewSkill = (id) => {
    selectedSkillId.value = id;
    viewOnly.value = true;
    showEditModal.value = true;
};

// Watch for modal closing to refresh skills list
watch(() => showEditModal.value, (isOpen) => {
    if (!isOpen) {
        // Refresh skills list when modal closes
        fetchSkills(pagination.value.currentPage || 1);
    }
});

const deleteSkill = async () => {
    if (!selectedSkill.value) return;
    deleteError.value = null;
    deleteErrorType.value = null;

    try {
        const success = await store.dispatch("deleteSkill", selectedSkill.value.id);
        if (success) {
            toast.success("Skill deleted successfully!", { position: "top-right" });
            await fetchSkills(pagination.value.currentPage || 1);
        } else {
            toast.error("Failed to delete skill", { position: "top-right" });
        }
    } catch (error) {
        console.error("Error deleting skill:", error);
        // Extract meaningful API message for persistent display
        const apiMsg = error?.message || '';
        const msgLower = apiMsg.toLowerCase();
        if (msgLower.includes('assigned') || msgLower.includes('cannot delete') || msgLower.includes('workgroup')) {
            deleteError.value = apiMsg;
            // Determine which case to show contextual help for
            if (msgLower.includes('workgroup')) {
                deleteErrorType.value = 'workgroups';
            } else if (msgLower.includes('user') || msgLower.includes('assigned')) {
                deleteErrorType.value = 'users';
            } else {
                deleteErrorType.value = 'generic';
            }
        } else {
            toast.error(apiMsg || "An error occurred while deleting the skill", { position: "top-right" });
        }
    } finally {
        showDeleteModal.value = false;
        selectedSkill.value = null;
    }
};

onMounted(async () => {
    await fetchSkills();
});
</script>

<style scoped>
.delete-error-overlay {
  position: fixed;
  inset: 0;
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.45);
  animation: fadeIn .2s;
}
.delete-error-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.18);
  max-width: 480px;
  width: 92%;
  overflow: hidden;
  animation: slideUp .25s ease-out;
}
.delete-error-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #ef5350 0%, #c62828 100%);
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
}
.delete-error-header i { font-size: 22px; }
.delete-error-close {
  margin-left: auto;
  background: none;
  border: none;
  color: #fff;
  font-size: 22px;
  cursor: pointer;
  line-height: 1;
  opacity: .8;
}
.delete-error-close:hover { opacity: 1; }
.delete-error-body {
  padding: 24px 20px 12px;
  text-align: center;
}
.delete-error-icon-wrap {
  margin-bottom: 12px;
}
.delete-error-icon-wrap i {
  font-size: 48px;
  color: #ef5350;
}
.delete-error-msg {
  font-size: 1.05rem;
  font-weight: 600;
  color: #344767;
  margin-bottom: 16px;
}
.delete-error-help {
  text-align: left;
  background: #f8f9fa;
  border-radius: 10px;
  padding: 16px;
}
.delete-error-help h6 {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  color: #1976d2;
  margin-bottom: 10px;
  font-size: 0.88rem;
}
.delete-error-help h6 i { font-size: 18px; }
.delete-error-help ol {
  padding-left: 1.25rem;
  margin-bottom: 12px;
  font-size: 0.85rem;
  color: #495057;
}
.delete-error-help ol li {
  margin-bottom: 6px;
}
.delete-error-note {
  font-size: 0.78rem;
  color: #7b809a;
  margin: 0;
  padding: 8px 10px;
  background: #e8f4fd;
  border-radius: 6px;
  border-left: 3px solid #1976d2;
}
.delete-error-footer {
  padding: 12px 20px 16px;
  text-align: center;
}
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
</style>
